// src/pages/public/Services/ServiceDetail/ServiceDetailPage.jsx
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppDispatch.js'
import { fetchServiceBySlug } from '../../../../redux/slices/servicesSlice.js'
import { fetchCaseStudies } from '../../../../redux/slices/caseStudiesSlice.js'
import { fetchTestimonials } from '../../../../redux/slices/testimonialsSlice.js'
import SEO from '../../../../components/seo/SEO.jsx'
import BuildSolutionForm from '../../../../components/forms/BuildSolutionForm.jsx'
import CaseStudyListSection from '../../../../components/sections/CaseStudyListSection.jsx'
import TestimonialsCarousel from '../../../../components/sections/TestimonialsCarousel.jsx'
import { LazyImage } from '../../../../components/common/LazyImage.jsx'
import ErrorBlock from '../../../../components/common/ErrorBlock.jsx'
import SkeletonSection from '../../../../components/common/SkeletonSection.jsx'
import Cards from "../../../../components/common/Cards.jsx"
import HomeChallengesPreview from '../../../../components/sections/HomeChallengesPreview.jsx'
import { openModal } from '../../../../redux/slices/modalSlice.js';

import { motion, AnimatePresence } from 'framer-motion'
import {
  FaArrowRight,
  FaHome,
  FaPlus,
  FaMinus
} from 'react-icons/fa'

import './ServiceDetailPage.scss'
import BuildSolution from '../../../../components/common/Common/BuildSolution/BuildSolution.jsx'

export default function ServiceDetailPage() {
  const { slug } = useParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handleOpenContactModal = () => {
    dispatch(openModal({
      modalType: 'contact',
      modalProps: {}
    }));
  };

  const {
    currentService: service,
    loading,
    error
  } = useAppSelector((state) => state.services)
  const { caseStudies } = useAppSelector((state) => state.caseStudies)
  const { testimonials } = useAppSelector((state) => state.testimonials)

  useEffect(() => {
    if (slug) {
      dispatch(fetchServiceBySlug(slug))
      dispatch(fetchCaseStudies({ limit: 3 }))
      dispatch(fetchTestimonials({ limit: 6 }))
    }
  }, [dispatch, slug])

  if (loading) {
    return (
      <>
        <SEO title="Loading Service..." />
        <div className="space-y-20">
          <SkeletonSection />
          <SkeletonSection />
          <SkeletonSection />
        </div>
      </>
    )
  }

  if (error || !service) {
    return (
      <>
        <SEO title="Service Not Found" />
        <div className="container-custom section-padding">
          <ErrorBlock
            title="Service Not Found"
            message="The service you're looking for doesn't exist or has been moved."
            onRetry={() => slug && dispatch(fetchServiceBySlug(slug))}
          />
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title={service.seoTitle || service.name}
        description={service.seoDescription || service.shortDescription}
        image={service.hero?.imageUrl || service.cardImageUrl}
        type="article"
      />

      <div className="service-detail-page">
        {/* HERO */}
        {service.hero?.isVisible !== false && (
          <HeroSection handleOpenContactModal={handleOpenContactModal} service={service} navigate={navigate} />
        )}

        {/* WHY NEED / BENEFITS (accordion) */}
        {service.whyNeed?.isVisible !== false && (
          <WhyNeedSection service={service} />
        )}

        {/* WHAT WE DO – vertical image cards */}
        {service.whatWeDo?.isVisible !== false &&
          service.whatWeDo?.cards &&
          service.whatWeDo.cards.length > 0 && (
            <OurSolutionsSection service={service} />
          )}

        {/* PROOF CTA */}
        {service.proof?.isVisible !== false && (
          <ProofSection service={service} />
        )}

        {/* STAGE PROCESS – 4 stage grid */}
        {service.stageProcess?.isVisible !== false &&
          service.stageProcess?.cards &&
          service.stageProcess.cards.length > 0 && (
            <StageProcessSection service={service} />
          )}



        {/* TESTIMONIALS */}
        {/* <section className="service-testimonials-section">
          <div className="service-testimonials-container"> */}

        <TestimonialsCarousel testimonials={testimonials} loading={false} />
        {/* </div>
        </section> */}

        <BuildSolution />

        {/* OPTIONAL TECH / CARDS SECTION */}
        {service.cardsSection?.isVisible !== false && (
          <CardsSection service={service} />
        )}
      </div>
    </>
  )
}

/* -------------------------- HERO SECTION (v1 style) -------------------------- */

const HeroSection = ({ service, navigate, handleOpenContactModal }) => {
  const [animationKey, setAnimationKey] = useState(0)

  useEffect(() => {
    setAnimationKey((prev) => prev + 1)
  }, [service?._id])

  const hero = service.hero || {}

  const heading = (hero.heading || service.name || '').toUpperCase()
  const subheading = hero.subheading || service.shortDescription
  const heroImage = hero.imageUrl || service.heroImageUrl || service.cardImageUrl

  return (
    <section className="industry-hero-section-service" key={animationKey}>
      <div className="industry-hero-container">
        {/* Breadcrumb */}
        <motion.div
          className="breadcrumb"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="breadcrumb-item" onClick={() => navigate('/')}>
            <FaHome className="breadcrumb-icon" />
            Home
          </span>
          <span className="breadcrumb-divider">/</span>
          <span className="breadcrumb-item" onClick={() => navigate('/services')}>
            Services
          </span>
          <span className="breadcrumb-divider">/</span>
          <span className="breadcrumb-item active">{service.name}</span>
        </motion.div>

        {/* Heading */}
        <motion.h1
          className="industry-heading"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {heading || 'SERVICE'}
        </motion.h1>

        {/* Subheading */}
        {subheading && (
          <motion.p
            className="industry-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {subheading}
          </motion.p>
        )}

        {/* Button */}
        {hero.buttonText && (
          <motion.button
            className="industry-button"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            onClick={() => {
              handleOpenContactModal()
            }}
            whileHover={{
              scale: 1.05,
              backgroundColor: 'var(--primary)',
              color: '#000'
            }}
            whileTap={{ scale: 0.98 }}
          >
            {hero.buttonText}
            <FaArrowRight className="button-icon" />
          </motion.button>
        )}

        {/* Hero Image Card */}
        {heroImage && (
          <motion.div
            className="card-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="card-background">
              <LazyImage
                src={heroImage}
                alt={service.name}
                className="card-bg-image"
                placeholder={<div className="card-bg-placeholder" />}
              />
              <div className="card-overlay"></div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  )
}

/* -------------------------- WHY NEED (accordion) -------------------------- */

const WhyNeedSection = ({ service }) => {
  const [openIndex, setOpenIndex] = useState(null)
  const data = service.whyNeed || {}

  const toggleCard = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  const cards =
    data.questions?.map((qa, index) => ({
      number: `0${index + 1}`,
      title: qa.question,
      content: qa.answer
    })) || []

  if (!data || (!data.paraOne && !data.paraTwo && cards.length === 0)) return null

  return (
    <section className="expandable-section-cap">
      <div className="expandable-container">
        {/* Capsule */}
        {data.capsuleText && (
          <motion.div
            className="centered-capsule"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {data.capsuleText}
          </motion.div>
        )}

        {/* Heading */}
        {data.heading && (
          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {data.heading.toUpperCase()}
          </motion.h2>
        )}

        {/* Paragraphs */}
        {(data.paraOne || data.paraTwo) && (
          <motion.div
            className="section-paragraphs"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {data.paraOne && <p className="first-para">{data.paraOne}</p>}
            {data.paraTwo && <p className="second-para">{data.paraTwo}</p>}
          </motion.div>
        )}

        {/* Cards */}
        {cards.length > 0 && (
          <motion.div
            className="cards-grid"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="grid-row">
              {cards.slice(0, 2).map((card, index) => (
                <ExpandableCard
                  key={index}
                  card={card}
                  index={index}
                  isOpen={openIndex === index}
                  toggleCard={toggleCard}
                />
              ))}
            </div>

            {cards.length > 2 && (
              <div className="grid-row">
                {cards.slice(2, 4).map((card, index) => (
                  <ExpandableCard
                    key={index + 2}
                    card={card}
                    index={index + 2}
                    isOpen={openIndex === index + 2}
                    toggleCard={toggleCard}
                  />
                ))}
              </div>
            )}

            {cards.length > 4 && (
              <div className="grid-row">
                {cards.slice(4, 6).map((card, index) => (
                  <ExpandableCard
                    key={index + 4}
                    card={card}
                    index={index + 4}
                    isOpen={openIndex === index + 4}
                    toggleCard={toggleCard}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  )
}

const ExpandableCard = ({ card, index, isOpen, toggleCard }) => {
  return (
    <motion.div
      className={`expandable-card ${isOpen ? 'open' : ''}`}
      initial={false}
      animate={{ height: isOpen ? 'auto' : '80px' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="card-header" onClick={() => toggleCard(index)}>
        <span className="card-number">{card.number}.</span>
        <h3 className="card-title">{card.title}</h3>
        <button className="toggle-button">
          {isOpen ? <FaMinus /> : <FaPlus />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="card-content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="card-description">{card.content}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

/* -------------------------- OUR SOLUTIONS (WHAT WE DO) -------------------------- */

const OurSolutionsSection = ({ service }) => {
  const data = service.whatWeDo
  if (!data || !data.cards || !data.cards.length) return null

  const cardsData =
    data.cards?.map((card, index) => ({
      id: index + 1,
      number:
        `0${index + 1}`, // extract number if present
      image: card.imageUrl,
      heading:
        card.Heading?.replace(/^\d+\.\s*/, '') || `Solution ${index + 1}`,
      para1: card.SubHeading || '',
      listItems: card.list || []
    })) || []

  return (
    <section className="vertical-cards-section-service">
      <div className="vertical-cards-container">
        {data.capsuleText && (
          <motion.div
            className="centered-capsule"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {data.capsuleText}
          </motion.div>
        )}

        {data.Heading && (
          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {data.Heading.toUpperCase()}
          </motion.h2>
        )}

        <div className="vertical-cards">
          {cardsData.map((card, index) => (
            <motion.div
              key={card.id}
              className="process-card"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <div className="card-image">
                {card.image && (
                  <LazyImage
                    src={card.image}
                    alt={card.heading}
                    placeholder={<div className="card-image-placeholder" />}
                  />
                )}
                <div className="image-overlay"></div>
                {/* <span className="card-number">{card.number}</span> */}
              </div>

              <div className="card-content">
                <h3 className="card-heading">
                  <span className="heading-number">{card.number}.</span>
                  {card.heading}
                </h3>
                {card.para1 && <p className="card-para">{card.para1}</p>}

                {card.listItems.length > 0 && (
                  <ul className="card-list">
                    {card.listItems.map((item, itemIndex) => (
                      <li key={itemIndex} className="list-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------------------- PROOF CTA -------------------------- */

const ProofSection = ({ service }) => {
  const proof = service.proof || {}
  if (!proof.Heading && !proof.ButtonText && !proof.capsuleText) return null

  return (
    <section >
      {/* <div className="service-proof-container"> */}
      {/* {proof.capsuleText && (
          <motion.div
            className="centered-capsule"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {proof.capsuleText}
          </motion.div>
        )}

        {proof.Heading && (
          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {proof.Heading}
          </motion.h2>
        )} */}

      <HomeChallengesPreview sectionData={proof} />

      {/* {proof.ButtonText && (
          <motion.a
            href={proof.ButtonLink || '#build-solution'}
            className="service-proof-button"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
          >
            {proof.ButtonText}
            <FaArrowRight className="button-icon" />
          </motion.a>
        )} */}
      {/* </div> */}
    </section>
  )
}

/* -------------------------- STAGE PROCESS -------------------------- */

const StageProcessSection = ({ service }) => {
  const sectionData = service.stageProcess
  if (!sectionData || !sectionData.cards || !sectionData.cards.length)
    return null

  const resultsData =
    sectionData.cards?.map((card, index) => ({
      id: index + 1,
      number: `0${index + 1}`,
      title: card.heading,
      description: card.subheading
    })) || []

  return (
    <section className="results-delivered-section-service">
      <div className="results-container">
        {sectionData.capsuleText && (
          <motion.div
            className="centered-capsule"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {sectionData.capsuleText}
          </motion.div>
        )}

        {sectionData.Heading && (
          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {sectionData.Heading.toUpperCase()}
          </motion.h2>
        )}

        <div className="results-grid">
          <motion.div
            className="results-row"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {resultsData.slice(0, 2).map((result) => (
              <motion.div
                key={result.id}
                className="result-card-service"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="card-image-row">{result.number}</div>
                <div className="card-content-row">
                  <p className="result-description">{result.title}</p>
                </div>
                <p className="result-description">{result.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {resultsData.length > 2 && (
            <motion.div
              className="results-row"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              {resultsData.slice(2, 4).map((result) => (
                <motion.div
                  key={result.id}
                  className="result-card-service"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="card-image-row">{result.number}</div>
                  <div className="card-content-row">
                    <p className="result-description">{result.title}</p>
                  </div>
                  <p className="result-description">{result.description}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  )
}

/* -------------------------- CARDS SECTION (optional) -------------------------- */

const CardsSection = ({ service }) => {
  const data = service.cardsSection || {}
  if (!data.Heading) return null

  return (
    <section className="service-cards-section">
      <div className="service-cards-container">
        {data.capsuleText && (
          <motion.div
            className="centered-capsule"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {data.capsuleText}
          </motion.div>
        )}

        <motion.h2
          className="section-heading"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {data.Heading?.toUpperCase()}
        </motion.h2>
        <Cards />

      </div>
    </section>
  )
}
